import { updateField } from "vuex-map-fields";
import router from "../router";
import Term from "@/models/term";
import PaymentMethod from "@/models/payment-method";
import Payer from "@/models/payer";
import User from "@/models/user";
import Invoice from "@/models/invoice";
import Merchant from "@/models/merchant";
import PendoUtil from "@/utils/pendo";

export default {
  setUserPassword: (state, payload) => {
    state.createPasswordUrl = payload.createPasswordUrl;
  },

  transactionError: (state, payload) => {
    state.invoice.errorMessage = payload;
  },

  setExtraDataLoadingError: (state, hasError) => {
    state.extraDataLoadingError = hasError;
  },

  clearUserPaymentMethods: (state) => {
    state.payer.paymentMethods = [];
  },

  savePaymentMethod: (state, paymentMethodData) => {
    const paymentMethod = new PaymentMethod(paymentMethodData);
    const existingIndex = state.payer.paymentMethods.findIndex(
      (existingPaymentMethod) => existingPaymentMethod.id === paymentMethod.id
    );
    if (existingIndex === -1) {
      state.payer.paymentMethods.push(paymentMethod);
    } else {
      state.payer.paymentMethods.splice(existingIndex, 1, paymentMethod);
    }
  },

  removePaymentMethod: (state, paymentMethodId) => {
    const paymentMethodIndex = state.payer.paymentMethods.findIndex(
      (paymentMethod) => paymentMethod.id === paymentMethodId
    );
    if (paymentMethodIndex !== -1) {
      state.payer.paymentMethods.splice(paymentMethodIndex, 1);
      if (state.payer.paymentMethods[paymentMethodIndex].isBankDebit) {
        state.scheduledPaymentDate = "";
      }
    }
  },

  selectPaymentMethod: (state, id) => {
    state.selectedPaymentMethodIndex = state.payer.paymentMethods.findIndex((paymentMethod) => paymentMethod.id === id);
    if (state.selectedPaymentMethodIndex !== -1) {
      const selectedPaymentMethod = state.payer.paymentMethods[state.selectedPaymentMethodIndex];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (
        !selectedPaymentMethod.savedInWP ||
        (selectedPaymentMethod.isCreditCard &&
          state.scheduledPaymentDate &&
          state.scheduledPaymentDate.valueOf() === today.valueOf())
      ) {
        state.scheduledPaymentDate = "";
      } else if (selectedPaymentMethod.isBankDebit && !state.scheduledPaymentDate) {
        state.scheduledPaymentDate = today;
      }
    } else {
      state.selectedPaymentMethodIndex = null;
      state.scheduledPaymentDate = "";
    }
  },

  transactionDataInit: (state, payload) => {
    state.tokenization = payload.tokenization;
    state.merchant = new Merchant(payload);
    state.isVirtualTerminal = payload.isVirtualTerminal;
    state.thirdPartyIntegration = payload.thirdPartyIntegration;
    state.allowPartPayment = payload.allowPartPayment;
    state.isNewPrepaymentFlowEnabled = payload.isNewPrepaymentFlowEnabled;
    state.isGuestBankPaymentAccepted = payload.isGuestBankPaymentAccepted;
    state.isGuestCreditCardPaymentAccepted = payload.isGuestCreditCardPaymentAccepted;
    state.isBaseCurrency = payload.isBaseCurrency;
    state.isCardPresentPaymentsEnabled = payload.isCardPresentPaymentsEnabled;
    state.accountId = payload?.accountId ?? "";
    state.accountName = payload?.accountName ?? "";

    const invoiceData = {
      paymentCompany: payload.paymentCompanyName,
      payerCompany: payload.payerCompanyName,
      scheduledAmount: payload.pendingAmount,
      isInvoiceLocked: payload.isInvoiceLocked,
      timezone: payload.timeZone,
      hasOutstandingInvoices: payload.hasOutstandingInvoices,
    };

    if (payload.invoiceDetails) {
      Object.assign(invoiceData, payload.invoiceDetails);
    } else {
      Object.assign(invoiceData, {
        invoiceNumber: payload.invoiceId,
        total: payload.amountToPay,
        amountDue: payload.totalDue,
        isSynced: false,
      });
    }

    state.invoice = new Invoice(invoiceData);

    if (!state.payer.company && state.invoice.payerCompany) {
      state.payer.company = state.invoice.payerCompany;
    }
  },

  paymentInProgress: (state, payload) => {
    state.paymentInProgress = payload;
  },

  logoutUser: (state) => {
    state.payingToday = 0;
    state.surcharge = 0;
    state.scheduledPaymentDate = "";
    state.selectedPaymentMethod = {};
    state.user = new User();
    state.payer = new Payer();
    state.isRegisteringNewPayer = false;
    state.payment.paymentStatus = false;
  },

  paymentSuccessful: (state) => {
    state.payment.paymentStatus = true;

    router.push({
      name: "paymentSuccess",
      query: {
        transactionId: state.transactionId,
      },
    });
  },

  paymentError: (state, payload) => {
    state.payment.paymentStatus = "error";
    state.payment.paymentErrorMessage = payload;

    router.push({
      name: "paymentFail",
      query: {
        transactionId: state.transactionId,
      },
    });
  },

  // Redirects via the `returnUrl` along with `ExternalId` and `Success` as params
  paymentThirdPartyReturnUrl: (state) => {
    state.payment.paymentStatus = true;
    state.redirectInProgress = true;
    let route = [
      // Main URL
      `${state.thirdPartyIntegration?.returnUrl}`,
      // Params
      `PaymentToken=${state.payment?.transactionResult?.ExternalId ?? ""}`,
      `IsSuccess=${state.payment?.transactionResult?.IsSuccess ?? false}`,
    ];

    window.location.href = route.join("&");
  },

  updateField,

  userLogin: (state, userData) => {
    //this is the function called from the login modal
    // setting the visiility of the panels

    state.isRegisteringNewPayer = false;
    state.user = new User(userData);
    state.termsAndConditions.acceptedBy = userData.name;
  },
  continueAsGuest: (state) => {
    state.isRegisteringNewPayer = false;
  },
  userRegister: (state) => {
    state.isRegisteringNewPayer = true;
  },
  setCheckoutStep: (state, payload) => {
    state.checkoutStep = payload;
  },
  setPayTodayAmount: (state) => {
    state.payingToday = state.invoice.amountDue;
  },

  setTermsAndConditions: (state, terms) => {
    if (terms && terms.length) {
      terms.forEach(function (term) {
        const newTerm = new Term(term);
        const existingIndex = state.termsAndConditions.terms.findIndex(
          (existingTerm) => existingTerm.id === newTerm.id
        );
        if (existingIndex !== -1) {
          state.termsAndConditions.terms[existingIndex] = newTerm;
        } else {
          state.termsAndConditions.terms.push(newTerm);
        }
      });
    }
  },

  updateTermDetails: (state, termDetail) => {
    const term = state.termsAndConditions.terms.find((term) => term.id === termDetail.TermsAndConditionsId);
    term.update(termDetail);
  },

  acceptTerm: (state, termId) => {
    const term = state.termsAndConditions.terms.find((term) => term.id === termId);
    if (term) {
      term.accepted = true;
    }
  },

  saveJWT: (state, payload) => {
    state.jwt = payload;
    state.jwtTimestamp = new Date();
  },

  resetJWTWithTimestamp: (state) => {
    state.jwt = "";
    state.jwtTimestamp = null;
  },

  setTransactionId: (state, payload) => {
    state.transactionId = payload;
  },

  setPayerPaymentMethods: (state, paymentMethods) => {
    if (paymentMethods && paymentMethods.length) {
      state.payer.paymentMethods = paymentMethods.map((paymentMethodData) => {
        const paymentMethod = new PaymentMethod(paymentMethodData);
        paymentMethod.savedInWP = true;
        return paymentMethod;
      });
    } else {
      state.payer.paymentMethods = [];
    }
  },

  setPayersList: (state, payload) => {
    payload.forEach(function (e) {
      e.fullname = e.firstName + " " + e.lastName;
    });
    state.user.payersList = payload;
  },

  setPayerDetails: (state, payload) => {
    state.payer.userId = payload.userId ?? state.payer.userId;
    state.payer.firstName = payload.firstName ?? state.payer.firstName;
    state.payer.lastName = payload.lastName ?? state.payer.lastName;
    state.payer.phoneNumber = payload.contactNumber ?? state.payer.phoneNumber;
    state.payer.email = payload.email ?? state.payer.email;
    state.payer.company = payload.company || state.invoice.payerCompany;
    state.payer.payerProfiles = payload.payerProfiles ?? state.payer.payerProfiles;

    // Initialize Pendo for guest users
    const visitorId = state.payer?.userId ?? state.user.id ?? "";
    const payerCompany = state.payer?.company ?? state.merchant?.name ?? "";
    const accountId = state.accountId ?? "";
    const accountName = state.accountName ?? "";
    const merchantId = state.merchant?.id ?? "";
    const merchantName = state.merchant?.name ?? "";

    const userEmail = state.user?.email ?? "";
    const userName = state.user?.username ?? "";
    const role = userEmail === "" && userName == "" ? "guest" : state.user.role;
    const email = userEmail !== "" ? userEmail : state.payer?.email ?? "";
    const name = role === "guest" ? `${state.payer?.firstName} ${state.payer?.lastName}` : userName;

    PendoUtil.initializePendo(
      email,
      name,
      role,
      visitorId,
      payerCompany,
      accountId,
      merchantId,
      merchantName,
      accountName
    );
  },

  clearPayerDetails: (state) => {
    state.payer = new Payer();

    state.payer.company = state.invoice.payerCompany;
  },

  setIsRegistering: (state, flag) => {
    state.isRegisteringNewPayer = flag;
  },

  setPayerId: (state, id) => {
    state.payer.userId = id;
  },

  overrideSurcharge: (state, payload) => {
    state.merchant.overrideSurcharge = payload;
  },

  setLoadingInvoiceDetails: (state, isLoading) => {
    state.loadingInvoiceDetails = isLoading;
  },
  setLoadingTerms: (state, isLoading) => {
    state.loadingTerms = isLoading;
  },
  setLoadingUserDetails: (state, isLoading) => {
    state.loadingUserDetails = isLoading;
  },
  setLoadingPaymentMethods: (state, isLoading) => {
    state.loadingPaymentMethods = isLoading;
  },
  setIsBillingDetailsUpdated: (state, isUpdated) => {
    state.isBillingDetailsUpdated = isUpdated;
  },
  setOnboardingToken: (state, token) => {
    state.onboardingToken = token;
    state.transactionId = token;
  },
  setOnboardingAsExpired: (state) => {
    state.isOnboardingExpired = true;
  },
  initializeOnboardingData: (state, payload) => {
    state.merchant = new Merchant(payload);
    state.tokenization = payload.tokenization;
    state.onboardingTermsId = payload.payerOnboardingTermsId;
    state.isUserActivated = payload.isUserActivated;
    state.partnerPortalUrl = payload.partnerPortalUrl;
    if (!state.payer.company && payload.payerCompanyName) {
      state.payer.company = payload.payerCompanyName;
    }
  },
  setLoadingOnboardingData: (state, isLoading) => {
    state.isLoadingOnboardingData = isLoading;
  },
  setLoadingOnboardingDataError: (state, isError) => {
    state.isLoadingOnboardingDataError = isError;
  },
  setAutoPaymentTermsToOtherPayerStatus: (state, isAssignedToOtherPayer) => {
    state.isAutoPaymentTermsAssignedToOtherPayer = isAssignedToOtherPayer;
  },

  // Push alert to its assigned key
  setAlertDetailsByKey: (state, payload) => {
    state.alertGroups[payload.alertReferenceKey] = payload.details ? [payload.details] : [];
  },
  // Remove all alerts under a specific key
  clearAlertDetailsByKey: (state, key) => {
    state.alertGroups[key] = [];
  },

  // Set userDetailsStep data
  setUserDetailsStepData: (state, payload) => {
    state.userDetailsStep = payload;
  },

  setAvsBillingDetailsForZeroQuoteFlow: (state, payload) => {
    state.avsBillingDetailsForZeroQuoteFlow = payload;
  },

  setMerchantPayerCompanies: (state, payload) => {
    state.merchantPayerCompanies = payload;
  },
  setIsPaymentDetailsInEdit: (state, payload) => {
    state.isPaymentDetailsInEdit = payload;
  },
  setStepCardState: (state, { key, value = false }) => {
    state.stepCardStates[key].isDisplayed = value;
  },
};
